// Front-view non-muscle structure layer for pain capture
// (PAIN_TRACKING_PLAN.md Task 2.2). React-free / JSX-free (hazard H7).
//
// Hand-authored PainRegion[] on the existing 220x520 front canvas
// (regionsFront.ts), coordinates chosen to sit over the correct anatomy in the
// muscle artwork. Only the screen-left half of each paired structure is written;
// pairPainRegions derives the "-r" twin with mirrorPath (axis x = 110), so the
// two halves cannot drift apart. Unpaired midline structures (the sternum, the
// six visceral quadrants) are single centerPainRegions.
//
// Joints, bone landmarks and visceral quadrants are drawn as ellipses via
// ellipsePath — a tap target over the region, not a rendering of its shape.
// Nerve tracks and dermatome bands are hand-authored thin closed curves along
// the path the nerve / skin band actually runs.
//
// Coverage (Task 2.2): shoulder / elbow / wrist / hip / knee / ankle joints;
// MCP / PIP / DIP blocks; clavicle, sternum, ribs, patella, tibial shaft; the
// six visceral quadrants; median / ulnar / radial nerve tracks; C5-T1 dermatome
// bands; TMJ.

import type { PainRegion } from "./painRegions.js";
import { centerPainRegion, ellipsePath, pairPainRegions } from "./painRegions.js";

export const FRONT_STRUCTURES: PainRegion[] = [
  // ── joints ─────────────────────────────────────────────────────────────────
  ...pairPainRegions("joint-shoulder", ellipsePath(56, 116, 9, 10)),
  ...pairPainRegions("joint-elbow", ellipsePath(48, 196, 9, 9)),
  ...pairPainRegions("joint-wrist", ellipsePath(45, 256, 8, 7)),
  // Hip-joint pain is felt in the groin (not at the trochanter), so the mark
  // sits over the lower abdomen / groin rather than the outer hip.
  ...pairPainRegions("joint-hip", ellipsePath(95, 255, 9, 10)),
  ...pairPainRegions("joint-knee", ellipsePath(86, 364, 11, 12)),
  ...pairPainRegions("joint-ankle", ellipsePath(86, 481, 8, 7)),
  ...pairPainRegions("joint-tmj", ellipsePath(96, 58, 5, 6)),
  // Knuckle rows across the hand: MCP at the base of the fingers, then PIP, then
  // DIP toward the fingertips.
  ...pairPainRegions("joint-mcp", ellipsePath(43, 285, 12, 4)),
  ...pairPainRegions("joint-pip", ellipsePath(44, 294, 11, 3.5)),
  ...pairPainRegions("joint-dip", ellipsePath(45, 302, 10, 3)),

  // ── bone landmarks ─────────────────────────────────────────────────────────
  centerPainRegion("bone-sternum", ellipsePath(110, 135, 6, 42)),
  ...pairPainRegions(
    "bone-clavicle",
    "M 58 98 C 68 92 82 90 94 92 C 95 95 95 98 94 100 C 82 98 70 101 61 104 C 58 103 55 100 58 98 Z",
  ),
  ...pairPainRegions(
    "bone-rib",
    "M 70 126 C 73 140 79 155 90 165 C 92 164 93 161 92 158 C 83 149 78 137 75 125 C 74 122 71 123 70 126 Z",
  ),
  ...pairPainRegions("bone-patella", ellipsePath(86, 360, 9, 10)),
  ...pairPainRegions("bone-tibia-shaft", ellipsePath(86, 425, 4, 45)),

  // ── visceral quadrants (unpaired; each placed over its own quadrant) ────────
  centerPainRegion("visc-ruq", ellipsePath(99, 188, 11, 11)),
  centerPainRegion("visc-luq", ellipsePath(121, 188, 11, 11)),
  centerPainRegion("visc-rlq", ellipsePath(99, 232, 11, 11)),
  centerPainRegion("visc-llq", ellipsePath(121, 232, 11, 11)),
  centerPainRegion("visc-epigastric", ellipsePath(110, 180, 9, 9)),
  centerPainRegion("visc-suprapubic", ellipsePath(110, 250, 9, 8)),

  // ── nerve tracks ──────────────────────────────────────────────────────────
  // Median: down the middle of the forearm, through the carpal tunnel, into the
  // radial three and a half digits.
  ...pairPainRegions(
    "nerve-median",
    "M 55 214 C 52 228 49 244 46 262 C 45 269 45 275 46 280 C 48 280 49 274 50 266 C 52 248 55 230 57 216 C 58 213 56 212 55 214 Z",
  ),
  // Ulnar: behind the inner elbow, down the little-finger border.
  ...pairPainRegions(
    "nerve-ulnar",
    "M 61 196 C 58 210 56 226 55 244 C 55 254 55 264 56 272 C 58 272 59 264 59 254 C 60 238 62 220 64 204 C 65 199 63 194 61 196 Z",
  ),
  // Radial: the lateral (thumb-side) border of the forearm.
  ...pairPainRegions(
    "nerve-radial",
    "M 41 194 C 37 206 35 222 34 238 C 34 248 35 256 37 261 C 39 259 40 249 41 239 C 42 222 44 208 45 198 C 46 194 43 191 41 194 Z",
  ),

  // ── dermatome bands (educational overlay only) ────────────────────────────
  ...pairPainRegions(
    "derm-c5",
    "M 45 104 C 41 116 39 130 39 148 C 41 153 46 153 48 148 C 49 130 51 116 53 106 C 52 101 47 100 45 104 Z",
  ),
  ...pairPainRegions(
    "derm-c6",
    "M 41 208 C 37 222 35 238 35 256 C 37 263 41 263 43 256 C 44 238 46 222 48 210 C 47 206 43 205 41 208 Z",
  ),
  ...pairPainRegions(
    "derm-c7",
    "M 47 212 C 44 226 42 242 42 260 C 44 268 48 268 50 260 C 51 242 53 226 55 214 C 54 210 49 209 47 212 Z",
  ),
  ...pairPainRegions(
    "derm-c8",
    "M 53 214 C 51 228 50 244 51 262 C 53 270 57 270 58 262 C 59 244 60 228 61 216 C 60 212 55 211 53 214 Z",
  ),
  ...pairPainRegions(
    "derm-t1",
    "M 63 174 C 61 184 60 194 61 206 C 63 211 66 211 67 205 C 67 194 67 184 66 174 C 65 171 64 171 63 174 Z",
  ),
];
